import styled from 'styled-components';

/* Marketplace screens 02–05: detail, cart, checkout, confirmation. */

/* ── Shared brand tile + star row ── */
export const StyledBrandTile = styled.div<{ $hue: number; $size?: number; $radius?: string }>`
  width: ${({ $size }) => $size ?? 44}px;
  height: ${({ $size }) => $size ?? 44}px;
  border-radius: ${({ $radius }) => $radius ?? 'var(--r-md)'};
  flex: none;
  background: oklch(0.92 0.05 ${({ $hue }) => $hue});
  color: oklch(0.4 0.12 ${({ $hue }) => $hue});
  display: grid;
  place-items: center;
  font-weight: 800;
  font-size: ${({ $size }) => ($size ?? 44) * 0.34}px;
  letter-spacing: -0.02em;
`;

export const StyledStarRow = styled.span<{ $size?: number }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: var(--warning);
  font-size: ${({ $size }) => $size ?? 13}px;

  svg {
    width: ${({ $size }) => $size ?? 13}px;
    height: ${({ $size }) => $size ?? 13}px;
  }
`;

export const StyledStarValue = styled.span`
  color: var(--ink-2);
  font-weight: 600;
`;

/* ── Product detail (02) ── */
export const StyledDetailLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--s-4);
  min-width: 0;
`;

export const StyledGalleryGrid = styled.div`
  display: grid;
  grid-template-columns: 380px 1fr;
  gap: var(--s-5);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const StyledHeroImg = styled.div<{ $hue: number }>`
  aspect-ratio: 1 / 1;
  border-radius: var(--r-lg);
  background: var(--paper-2);
  display: grid;
  place-items: center;
  border: 1px solid var(--line);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: var(--s-5);
  }
`;

export const StyledThumbRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-top: 10px;
`;

export const StyledThumb = styled.div<{ $hue: number; $active?: boolean }>`
  aspect-ratio: 1 / 1;
  border-radius: var(--r-sm);
  background: var(--paper-2);
  display: grid;
  place-items: center;
  overflow: hidden;
  object-fit: contain;
  padding: 6px;
  box-sizing: border-box;
  border: ${({ $active }) =>
    $active ? '2px solid var(--brand-green)' : '1px solid var(--line)'};
  cursor: pointer;
`;

export const StyledDetailTitleCol = styled.div`
  min-width: 0;
`;

export const StyledBadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  flex-wrap: wrap;
`;

export const StyledDesc = styled.p`
  color: var(--muted);
  margin: 0 0 16px;
  font-size: var(--t-base);
  line-height: 1.6;
  max-width: 560px;
`;

export const StyledTierRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

export const StyledTierCard = styled.div<{ $active?: boolean }>`
  flex: 1 1 120px;
  padding: 12px 14px;
  border-radius: var(--r-md);
  cursor: pointer;
  border: 1px solid ${({ $active }) => ($active ? 'var(--brand-green)' : 'var(--line)')};
  background: ${({ $active }) => ($active ? 'var(--success-soft)' : 'var(--surface)')};
`;

export const StyledTierLabel = styled.div`
  font-size: var(--t-xs);
  color: var(--muted);
  font-weight: 600;
`;

export const StyledTierPrice = styled.div<{ $active?: boolean }>`
  font-size: var(--t-xl);
  font-weight: 700;
  color: ${({ $active }) => ($active ? 'var(--brand-green-ink)' : 'var(--ink)')};
  margin-top: 2px;
`;

export const StyledTierUnit = styled.div`
  font-size: 11px;
  color: var(--muted);
`;

export const StyledSpecGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 var(--s-5);

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

export const StyledKV = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 7px 0;
  border-bottom: 1px solid var(--line);
  font-size: var(--t-sm);
  gap: var(--s-3);

  span:first-child {
    color: var(--muted);
  }
  span:last-child {
    font-weight: 600;
  }
`;

export const StyledSupplierBody = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

export const StyledSupplierMeta = styled.div`
  flex: 1;
  min-width: 0;
`;

export const StyledSupplierName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  > span:first-child {
    font-weight: 700;
    font-size: var(--t-lg);
  }
`;

/* ── Buy box (02) ── */
export const StyledBuyPrice = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

export const StyledBuyPriceBig = styled.span`
  font-size: var(--t-4xl);
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--brand-green-ink);
`;

export const StyledStepper = styled.div<{ $h?: number }>`
  display: flex;
  align-items: stretch;
  border: 1px solid var(--line);
  border-radius: var(--r-md);
  overflow: hidden;
  height: ${({ $h }) => $h ?? 48}px;
`;

export const StyledStepBtn = styled.button<{ $w?: number }>`
  width: ${({ $w }) => $w ?? 48}px;
  border: 0;
  background: var(--surface);
  cursor: pointer;
  font-size: 22px;
  color: var(--ink-2);
  font-family: inherit;

  &:disabled {
    color: var(--muted-2);
    cursor: not-allowed;
  }
`;

export const StyledStepInput = styled.input`
  flex: 1;
  width: 100%;
  border: 0;
  border-left: 1px solid var(--line);
  border-right: 1px solid var(--line);
  text-align: center;
  font-size: var(--t-lg);
  font-weight: 700;
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  outline: none;
  color: var(--ink);
  background: var(--surface);
`;

export const StyledQuickSets = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 8px;
`;

export const StyledQuickSet = styled.button<{ $active?: boolean }>`
  flex: 1;
  padding: 6px;
  border-radius: var(--r-sm);
  cursor: pointer;
  font-family: inherit;
  border: 1px solid var(--line);
  background: ${({ $active }) => ($active ? 'var(--paper-2)' : 'var(--surface)')};
  font-size: 11px;
  font-weight: 600;
  color: var(--ink-2);
`;

export const StyledSubtotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-top: var(--s-5);
  padding: 14px 0;
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
`;

export const StyledBuyBtns = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: var(--s-4);
`;

/* Buy-box freight line — surfaces freight + free-over threshold BEFORE checkout. */
export const StyledFreightHint = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  font-size: var(--t-sm);
  color: var(--muted);

  svg {
    width: 15px;
    height: 15px;
    flex: none;
  }

  b {
    color: var(--ink-2);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
`;

export const StyledAssuranceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: var(--s-5);
`;

export const StyledAssurance = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: var(--t-sm);
  color: var(--ink-2);

  > span:first-child {
    width: 28px;
    height: 28px;
    border-radius: var(--r-sm);
    background: var(--success-soft);
    color: var(--success-ink);
    display: grid;
    place-items: center;
    flex: none;
  }
  svg {
    width: 15px;
    height: 15px;
  }
`;

export const StyledMaxNote = styled.div`
  font-size: var(--t-xs);
  color: var(--warning-ink);
  margin-top: 8px;
  font-variant-numeric: tabular-nums;
`;

/* ── Cart (03) ── */
export const StyledCartLine = styled.div<{ $divided?: boolean }>`
  display: flex;
  gap: 16px;
  padding: var(--s-4);
  align-items: center;
  border-bottom: ${({ $divided }) => ($divided ? '1px solid var(--line)' : 'none')};

  @media (max-width: 640px) {
    flex-wrap: wrap;
  }
`;

export const StyledCartThumb = styled.div<{ $hue: number; $size?: number }>`
  width: ${({ $size }) => $size ?? 72}px;
  height: ${({ $size }) => $size ?? 72}px;
  border-radius: var(--r-md);
  flex: none;
  font-size: ${({ $size }) => ($size ?? 72) / 2}px;
  background: oklch(0.95 0.03 ${({ $hue }) => $hue});
  display: grid;
  place-items: center;
  cursor: pointer;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 8px;
  }
`;

export const StyledCartMid = styled.div`
  flex: 1 1 160px;
  min-width: 0;
`;

export const StyledCartName = styled.div`
  font-weight: 600;
  font-size: var(--t-base);
  margin-top: 4px;
  overflow-wrap: break-word;
  word-break: break-word;
  hyphens: auto;
`;

export const StyledCartUnit = styled.div`
  color: var(--muted);
  font-size: var(--t-sm);
  margin-top: 2px;
`;

export const StyledQtyMini = styled.span`
  min-width: 48px;
  display: grid;
  place-items: center;
  border-left: 1px solid var(--line);
  border-right: 1px solid var(--line);
  font-weight: 700;
`;

export const StyledLineTotal = styled.div`
  text-align: end;
  min-width: 110px;

  > div:first-child {
    font-weight: 700;
    font-size: var(--t-base);
  }
  > div:last-child {
    font-size: 11px;
    color: var(--muted);
  }
`;

export const StyledRemoveBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: var(--r-sm);
  border: 1px solid var(--line);
  background: var(--surface);
  color: var(--muted);
  cursor: pointer;
  display: grid;
  place-items: center;
  flex: none;

  svg {
    width: 15px;
    height: 15px;
  }
  &:hover {
    color: var(--danger);
    border-color: var(--danger);
  }
`;

export const StyledSumRow = styled.div<{ $big?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 6px 0;

  > span:first-child {
    font-size: ${({ $big }) => ($big ? 'var(--t-base)' : 'var(--t-sm)')};
    font-weight: ${({ $big }) => ($big ? 700 : 500)};
    color: ${({ $big }) => ($big ? 'var(--ink)' : 'var(--muted)')};
  }
  > span:last-child {
    font-weight: ${({ $big }) => ($big ? 700 : 600)};
    font-size: ${({ $big }) => ($big ? 'var(--t-lg)' : 'var(--t-sm)')};
  }
`;

export const StyledDivider = styled.div`
  height: 1px;
  background: var(--line);
  margin: 10px 0;
`;

export const StyledSummaryFootnote = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  margin-top: 12px;
  font-size: var(--t-xs);
  color: var(--muted);

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const StyledSummaryNote = styled.div`
  font-size: var(--t-xs);
  color: var(--muted);
  margin: -2px 0 4px;
  line-height: 1.4;
`;

export const StyledEmptyCartCard = styled.div`
  text-align: center;
  padding: var(--s-10) var(--s-4);

  > div:first-child {
    width: 56px;
    height: 56px;
    border-radius: var(--r-lg);
    background: var(--paper-2);
    color: var(--muted);
    display: grid;
    place-items: center;
    margin: 0 auto var(--s-4);
  }
`;

/* ── Checkout (04) ── */
export const StyledCheckoutLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--s-4);
  min-width: 0;
`;

export const StyledStepRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: var(--s-5);
`;

export const StyledStep = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const StyledStepDot = styled.span<{ $active?: boolean; $done?: boolean }>`
  width: 26px;
  height: 26px;
  border-radius: var(--r-full);
  display: grid;
  place-items: center;
  font-size: var(--t-xs);
  font-weight: 700;
  background: ${({ $done, $active }) =>
    $done ? 'var(--brand-green)' : $active ? 'var(--brand-ink)' : 'var(--paper-2)'};
  color: ${({ $done, $active }) => ($done || $active ? 'var(--surface)' : 'var(--muted)')};

  svg {
    width: 13px;
    height: 13px;
  }
`;

export const StyledStepLabel = styled.span<{ $active?: boolean }>`
  font-size: var(--t-sm);
  font-weight: 600;
  color: ${({ $active }) => ($active ? 'var(--ink)' : 'var(--muted)')};
`;

export const StyledStepLine = styled.div<{ $done?: boolean }>`
  flex: 0 0 40px;
  height: 2px;
  border-radius: 1px;
  background: ${({ $done }) => ($done ? 'var(--brand-green)' : 'var(--line)')};
`;

export const StyledRadioGrid = styled.div<{ $cols?: number }>`
  display: grid;
  grid-template-columns: ${({ $cols }) => ($cols === 1 ? '1fr' : '1fr 1fr')};
  gap: 10px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

export const StyledRadioStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const StyledRadioCard = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  border-radius: var(--r-md);
  cursor: pointer;
  text-align: start;
  font-family: inherit;
  width: 100%;
  border: 1px solid ${({ $active }) => ($active ? 'var(--brand-green)' : 'var(--line)')};
  background: ${({ $active }) => ($active ? 'var(--success-soft)' : 'var(--surface)')};
`;

export const StyledRadioCircle = styled.span<{ $active?: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 9px;
  flex: none;
  border: 1px solid ${({ $active }) => ($active ? 'var(--brand-green)' : 'var(--line-2)')};
  background: ${({ $active }) => ($active ? 'var(--brand-green)' : 'var(--surface)')};
  display: grid;
  place-items: center;

  span {
    width: 6px;
    height: 6px;
    border-radius: 3px;
    background: var(--surface);
  }
`;

export const StyledRadioIcon = styled.span<{ $active?: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: var(--r-md);
  background: ${({ $active }) => ($active ? 'var(--success-soft)' : 'var(--paper)')};
  color: ${({ $active }) => ($active ? 'var(--success-ink)' : 'var(--muted)')};
  display: grid;
  place-items: center;
  flex: none;

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const StyledRadioText = styled.span`
  flex: 1;
  min-width: 0;

  > span:first-child {
    display: block;
    font-weight: 600;
    font-size: var(--t-sm);
  }
  > span:last-child {
    display: block;
    color: var(--muted);
    font-size: 11px;
    margin-top: 2px;
  }
`;

export const StyledAddressField = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  margin-top: 10px;
  padding: 10px 12px;
  border: 1px solid var(--line);
  border-radius: var(--r-md);
  background: var(--surface);
  font-family: inherit;
  font-size: var(--t-sm);
  color: var(--ink);
  resize: vertical;
  min-height: 64px;
  outline: none;

  &:focus {
    border-color: var(--brand-green);
  }
`;

export const StyledRecapHeader = styled.div`
  padding: 14px 20px;
  border-bottom: 1px solid var(--line);
  font-weight: 600;
`;

export const StyledRecapLine = styled.div<{ $divided?: boolean }>`
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 12px 20px;
  border-bottom: ${({ $divided }) => ($divided ? '1px solid var(--line)' : 'none')};
`;

export const StyledRecapThumb = styled.div<{ $hue: number }>`
  width: 40px;
  height: 40px;
  border-radius: var(--r-sm);
  background: oklch(0.95 0.03 ${({ $hue }) => $hue});
  display: grid;
  place-items: center;
  font-size: 20px;
  flex: none;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 4px;
  }
`;

export const StyledLegal = styled.div`
  color: var(--muted);
  font-size: 11px;
  text-align: center;
  margin-top: 10px;
  line-height: 1.5;
`;

/* ── Confirmation (05) ── */
export const StyledConfirmWrap = styled.div`
  max-width: 640px;
  margin: 0 auto;
  padding-top: var(--s-7);
`;

export const StyledSuccessCircle = styled.div`
  width: 72px;
  height: 72px;
  border-radius: var(--r-full);
  background: var(--success-soft);
  color: var(--brand-green);
  display: grid;
  place-items: center;
  margin: 0 auto var(--s-4);

  svg {
    width: 36px;
    height: 36px;
  }
`;

export const StyledReceiptHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: var(--s-4);
  border-bottom: 1px solid var(--line);
`;

export const StyledReceiptCode = styled.div`
  font-size: var(--t-lg);
  font-weight: 700;
  margin-top: 2px;
`;

export const StyledReceiptLine = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid var(--line);
`;

export const StyledReceiptTotal = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: var(--s-4);

  > span:first-child {
    font-weight: 700;
  }
  > span:last-child {
    font-weight: 700;
    font-size: var(--t-lg);
  }
`;

export const StyledConfirmBtns = styled.div`
  display: flex;
  gap: 10px;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

/* ── My Orders (06) ── */

export const StyledOrdersWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--s-4);
`;

export const StyledOrderCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--s-4);
`;

// Horizontal 5-step stage progress — sits inside each order card.
export const StyledStageTrack = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  width: 100%;
  padding: var(--s-3) 0 var(--s-2);
`;

export const StyledStageNode = styled.span<{ $done: boolean; $current: boolean }>`
  width: 22px;
  height: 22px;
  border-radius: var(--r-full);
  flex: none;
  border: 2px solid
    ${({ $done, $current }) =>
      $done || $current ? 'var(--brand-green)' : 'var(--line-2)'};
  background: ${({ $done, $current }) =>
    $done ? 'var(--brand-green)' : $current ? 'var(--success-soft)' : 'var(--paper-2)'};
  display: grid;
  place-items: center;

  svg {
    width: 11px;
    height: 11px;
    color: var(--surface);
  }
`;

export const StyledStageLine = styled.div<{ $done: boolean }>`
  flex: 1;
  height: 2px;
  background: ${({ $done }) => ($done ? 'var(--brand-green)' : 'var(--line)')};
  border-radius: 1px;
`;

export const StyledStageLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
`;

export const StyledStageLabel = styled.span<{ $current: boolean; $done: boolean }>`
  font-size: 10px;
  font-weight: ${({ $current }) => ($current ? 700 : 500)};
  color: ${({ $current, $done }) =>
    $current ? 'var(--brand-green-ink)' : $done ? 'var(--ink-2)' : 'var(--muted-2)'};
  text-align: center;
  flex: 1;
  line-height: 1.2;

  &:first-child { text-align: start; }
  &:last-child { text-align: end; }
`;

export const StyledOrderMeta = styled.div`
  display: flex;
  align-items: center;
  gap: var(--s-5);
  flex-wrap: wrap;
  padding-top: var(--s-3);
  border-top: 1px solid var(--line);
  font-size: var(--t-sm);
  color: var(--muted);
`;

export const StyledOrderMetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 5px;

  svg {
    width: 13px;
    height: 13px;
    flex: none;
  }

  b {
    color: var(--ink-2);
    font-weight: 600;
  }
`;
